import type { IApprovalRequestApiResponse, IApprovalRequest, ApprovalStatus } from "@/types/approvalTypes";

const REQUEST_STATUS_APPROVED = 3;
const REQUEST_STATUS_REJECTED = 4;

/**
 * Maps a numeric request status ID to a frontend ApprovalStatus.
 */
export function mapRequestStatus(requestStatusId: number): ApprovalStatus {
  if (requestStatusId === REQUEST_STATUS_APPROVED) {
    return "Approved";
  }
  if (requestStatusId === REQUEST_STATUS_REJECTED) {
    return "Rejected";
  }
  return "Pending";
}

/**
 * Formats an ISO 8601 date string to "MM/DD/YYYY HH:mm" for display.
 */
export function formatApprovalDate(isoDate: string): string {
  const date = new Date(isoDate);

  if (isNaN(date.getTime())) {
    return isoDate;
  }

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${month}/${day}/${year} ${hours}:${minutes}`;
}

/**
 * Transforms a single API response DTO into the frontend domain model.
 */
export function mapApiResponseToApprovalRequest(
  response: IApprovalRequestApiResponse,
): IApprovalRequest {
  const requestorName = response.createdByName ?? "Unknown";
  const requestorEmail = response.createdByEmail
    ? ` (${response.createdByEmail.split("@")[0]})`
    : "";

  return {
    id: response.id,
    partnerOneId: response.partnerOneId,
    partnerName: response.partnerName,
    requestor: `${requestorName}${requestorEmail}`,
    requestedOn: formatApprovalDate(response.createdDate),
    status: mapRequestStatus(response.requestStatusId),
  };
}

/**
 * Transforms a collection of API responses into frontend domain models.
 */
export function mapApiResponsesToApprovalRequests(
  responses: IApprovalRequestApiResponse[],
): IApprovalRequest[] {
  return responses.map(mapApiResponseToApprovalRequest);
}
